import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import CategoryHead from 'components/Category/Head';
import CategoryHeading from 'components/Category/Heading';
import CategoryOffersOptionsBox from 'components/Category/OptionsBox';
import CategorySchema from 'components/Category/Schema';
import Layout from 'components/Layout';
import LazyHydration from 'components/LazyHydration';
import PageDescription from 'components/PageDescription';
import OffersList from 'components/Timeline/OffersList';
import DynamicallyLoadedBlogArticles from 'components/pages/OfferPages/DynamicallyLoadedBlogArticles';

import useDialog from 'hooks/useDialog';
import useOffersFiltersState from 'hooks/useOffersFiltersState';
import useOffersSort from 'hooks/useOffersSort';

import { isOfferCategorySlugValidToActivateGoogleAd } from 'lib/ads';
import {
  getDeepSubcategoryParent,
  getOrderOffersGaEventAction,
  toHeadingTitle,
} from 'lib/category';
import { sendEvent } from 'lib/gtag';
import { offerQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';
import { runAndScrollToTop } from 'lib/utils';

import { useUtm } from 'providers/UtmProvider';

import { getSubcategoryOffersList } from 'services/category';

import SubcategorySidebar from './Sidebar';

import CATEGORY from 'constants/category';
import OFFER from 'constants/offer';

const FrequentAskedQuestions = dynamic(
  () => import('components/FrequentAskedQuestions')
);
const WishlistSuggestions = dynamic(
  () => import('components/Category/WishlistSuggestions')
);
const CategoryDialog = dynamic(() => import('components/Category/Dialog'), {
  ssr: false,
});

const GA_EVENT_CATEGORY = 'subcategory_page';
const ADS_LIST = [
  {
    adId: 'offer_subcategory_page_main_content_1_320x50_468x60_728x90',
    adPath:
      '/promobit.com.br_offer_subcategory_page/main_content_1_320x50_468x60_728x90',
    position: 8,
    isMobileOnly: false,
  },
  {
    adId: 'offer_subcategory_page_main_content_2_320x50_468x60_728x90',
    adPath:
      '/promobit.com.br_offer_subcategory_page/main_content_2_320x50_468x60_728x90',
    position: 19,
    isMobileOnly: true,
  },
];

const onNavItemClick = (name, gaEventCategory) => {
  sendEvent({
    category: gaEventCategory,
    action: 'subcategories',
    value: name,
  });
};

const onLoadMoreOffersButtonClick = (gaEventCategory) => {
  sendEvent({ category: gaEventCategory, action: 'load_more' });
};

const SubcategoryPage = ({
  featuredStores,
  isDeepSubcategory = false,
  serverOffers,
  subcategory,
}) => {
  const {
    articles,
    category,
    faqs,
    introText: subcategoryIntroText,
    metaDescription,
    metaTitle,
    pageDescription,
    stores,
    subcategoryId,
    subcategoryName,
    subcategorySlug,
    wishlistSuggestions = [],
  } = subcategory;

  const { showDialog } = useDialog();
  const { options, offersSort, setOffersSort } = useOffersSort();
  const [offersFilters, setOffersFilters] = useOffersFiltersState();
  const { isUtmLoaded } = useUtm();

  const deepSubcategoryParent = useMemo(
    () => getDeepSubcategoryParent(subcategory),
    [subcategory]
  );

  const isGoogleAdActive = useMemo(
    () => isOfferCategorySlugValidToActivateGoogleAd(category.categorySlug),
    [category.categorySlug]
  );

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/scar', {
        subcategory: subcategoryId,
      });
    }
  }, [subcategoryId, isUtmLoaded]);

  const onOffersFiltersReset = useCallback(
    () => setOffersFilters({ type: OFFER.FILTER_ACTION_TYPES.RESET }),
    []
  );

  const onOffersSortChange = ({ target: { value } }) => {
    sendEvent({
      category: GA_EVENT_CATEGORY,
      action: getOrderOffersGaEventAction(value),
    });
    setOffersSort(value);
  };

  const onOffersPriceRangeFilterChange = (priceRange) => {
    runAndScrollToTop(setOffersFilters, {
      type: OFFER.FILTER_ACTION_TYPES.PRICE_RANGE,
      priceRange,
    });
  };

  const onOffersStoresFilterChange = (storesFilter) => {
    runAndScrollToTop(setOffersFilters, {
      type: OFFER.FILTER_ACTION_TYPES.STORES,
      stores: storesFilter,
    });
  };

  const onOffersVenueFilterChange = (venue) => {
    runAndScrollToTop(setOffersFilters, {
      type: OFFER.FILTER_ACTION_TYPES.VENUE,
      venue,
    });
  };

  const onFilterButtonClick = () => {
    showDialog(CategoryDialog, {
      offersFilters,
      stores,
      categoryName: category.categoryName,
      categorySlug: category.categorySlug,
      gaEventCategory: GA_EVENT_CATEGORY,
      isSpaced: true,
      subcategories: category.subcategories,
      onOffersFiltersReset,
      onOffersPriceRangeFilterChange,
      onOffersStoresFilterChange,
      onOffersVenueFilterChange,
      onNavItemClick: () => onNavItemClick(GA_EVENT_CATEGORY),
    });
  };

  const queryFn = useCallback(
    ({ after }, opts) =>
      getSubcategoryOffersList(
        {
          after,
          filters: offersFilters,
          limit: CATEGORY.OFFERS.CLIENT_LIMIT,
          sort: offersSort,
          subcategorySlug,
        },
        opts
      ),
    [
      offersFilters.priceRange.min,
      offersFilters.priceRange.max,
      offersFilters.stores,
      offersFilters.venue,
      offersSort,
      subcategorySlug,
    ]
  );

  return (
    <>
      <CategoryHead
        categoryName={subcategoryName}
        metaDescription={metaDescription}
        metaTitle={metaTitle}
      />
      <CategorySchema
        categoryName={category.categoryName}
        categorySlug={category.categorySlug}
        deepSubcategoryName={isDeepSubcategory ? subcategoryName : null}
        deepSubcategorySlug={isDeepSubcategory ? subcategorySlug : null}
        offers={serverOffers.offers}
        subcategoryName={isDeepSubcategory ? null : subcategoryName}
        subcategorySlug={isDeepSubcategory ? null : subcategorySlug}
      />
      <Layout
        headingComponent={
          <CategoryHeading
            categoryIntroText={subcategoryIntroText}
            categoryName={category.categoryName}
            categorySlug={category.categorySlug}
            deepSubcategoryName={isDeepSubcategory ? subcategoryName : null}
            subcategoryName={
              isDeepSubcategory
                ? deepSubcategoryParent?.subcategoryName
                : subcategoryName
            }
            subcategorySlug={
              isDeepSubcategory
                ? deepSubcategoryParent?.subcategorySlug
                : subcategorySlug
            }
            title={toHeadingTitle(subcategoryName)}
          />
        }
        sidebarComponent={
          <SubcategorySidebar
            category={category}
            featuredStores={featuredStores}
            gaEventCategory={GA_EVENT_CATEGORY}
            isDeepSubcategory={isDeepSubcategory}
            isGoogleAdActive={isGoogleAdActive}
            offersFilters={offersFilters}
            position="left"
            stores={stores}
            subcategory={subcategory}
            onNavItemClick={onNavItemClick}
            onOffersFiltersReset={onOffersFiltersReset}
            onOffersPriceRangeFilterChange={onOffersPriceRangeFilterChange}
            onOffersStoresFilterChange={onOffersStoresFilterChange}
            onOffersVenueFilterChange={onOffersVenueFilterChange}
          />
        }
      >
        <CategoryOffersOptionsBox
          offersFilters={offersFilters}
          options={options}
          onFilterButtonClick={onFilterButtonClick}
          onOffersFiltersChange={setOffersFilters}
          onOffersSortChange={onOffersSortChange}
          onOffersVenueFilterChange={onOffersVenueFilterChange}
        />
        <OffersList
          cardTitleAs="h2"
          categoryName={
            isDeepSubcategory
              ? deepSubcategoryParent?.subcategoryName
              : category.categoryName
          }
          gaEventCategory={GA_EVENT_CATEGORY}
          googleAds={isGoogleAdActive ? ADS_LIST : []}
          initialOffers={serverOffers}
          offersFilters={offersFilters}
          queryFn={queryFn}
          queryKey={offerQueryKeys.listBySubcategory({
            subcategoryId,
            offersSort,
            offersFiltersStores: offersFilters.stores,
            offersFiltersMin: offersFilters.priceRange.min,
            offersFiltersMax: offersFilters.priceRange.max,
            offersFiltersVenue: offersFilters.venue,
          })}
          paginationCappedAtFinishedOffer
          showButtonBeforeFirstPagination
          onLoadMoreOffersButtonClick={() =>
            onLoadMoreOffersButtonClick(GA_EVENT_CATEGORY)
          }
          onOffersFiltersReset={onOffersFiltersReset}
        />
        {!isDeepSubcategory && wishlistSuggestions?.length > 0 && (
          <WishlistSuggestions
            gaEventCategory={GA_EVENT_CATEGORY}
            keywordButtonType="primary"
            keywordsDisplay={wishlistSuggestions}
            withEditWishlist
          />
        )}
        <DynamicallyLoadedBlogArticles
          articles={articles}
          className="mt-6"
          gaEventCategory={GA_EVENT_CATEGORY}
        />
        <LazyHydration placeholderSize={{ height: 300 }}>
          <PageDescription
            bg="transparent"
            className={twJoin(
              'mt-6 px-4 pb-6',
              faqs?.length > 0 ? 'mb-4' : 'mb-12'
            )}
            pageDescription={pageDescription}
          />
        </LazyHydration>
        {faqs?.length > 0 && (
          <FrequentAskedQuestions className="mb-12" faqs={faqs} />
        )}
      </Layout>
    </>
  );
};

SubcategoryPage.propTypes = {
  featuredStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeSlug: PropTypes.string,
      storeName: PropTypes.string,
    })
  ).isRequired,
  isDeepSubcategory: PropTypes.bool,
  subcategory: PropTypes.shape({
    articles: PropTypes.arrayOf(
      PropTypes.shape({
        articleId: PropTypes.number.isRequired,
        articleDescription: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
      })
    ),
    category: PropTypes.shape({
      categoryId: PropTypes.number.isRequired,
      categoryName: PropTypes.string.isRequired,
      categorySlug: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          isSelected: PropTypes.bool.isRequired,
          subcategoryId: PropTypes.number.isRequired,
          subcategoryName: PropTypes.string.isRequired,
          subcategorySlug: PropTypes.string.isRequired,
          deepSubcategories: PropTypes.arrayOf(
            PropTypes.shape({
              deepSubcategoryId: PropTypes.number.isRequired,
              deepSubcategoryName: PropTypes.string.isRequired,
              deepSubcategorySlug: PropTypes.string.isRequired,
              isSelected: PropTypes.bool.isRequired,
            })
          ),
        })
      ),
    }).isRequired,
    metaDescription: PropTypes.string,
    metaTitle: PropTypes.string,
    pageDescription: PropTypes.string,
    pageTitle: PropTypes.string,
    stores: PropTypes.arrayOf(
      PropTypes.shape({
        storeId: PropTypes.number.isRequired,
        storeName: PropTypes.string.isRequired,
      })
    ).isRequired,
    subcategoryId: PropTypes.number.isRequired,
    subcategoryName: PropTypes.string.isRequired,
    subcategorySlug: PropTypes.string.isRequired,
    wishlistSuggestions: PropTypes.arrayOf(PropTypes.string),
    introText: PropTypes.string,
  }),
  serverOffers: PropTypes.shape({
    after: PropTypes.string,
    sort: PropTypes.string,
    before: PropTypes.number,
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        categoryId: PropTypes.number,
        categoryName: PropTypes.string,
        categorySlug: PropTypes.string,
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeId: PropTypes.number,
        subcategoryId: PropTypes.number,
        subcategoryName: PropTypes.string,
        subcategorySlug: PropTypes.string,
      }).isRequired
    ).isRequired,
  }).isRequired,
};

export default SubcategoryPage;
